import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import Button from '@components/atoms/button/button';
import { CartContext } from '@context/cart.context';
import { ToastMessageContext } from '@context/toast-message.context';
import { useAnalytics } from '@context/analytics/analytics.context';
import Spinner from '@components/atoms/spinner/spinner';
import logger from '@helpers/utils/logger/client';
import clsx from 'clsx';
import useUser from '@hooks/user/use-user';
import { useRouter } from 'next/router';
import { Product } from '@interfaces/models/product';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Icon from '@components/atoms/icon/icon';
import Link from '@components/atoms/link/link';
import styles from './product-card-fav-actions.module.scss';

interface ProductCardFavActionsProps {
  product: Product;
}

const ProductCardFavActions: React.FC<ProductCardFavActionsProps> = (props) => {
  const { product } = props;
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { user } = useUser();
  const router = useRouter();
  const [atcLoading, setAtcLoading] = useState<boolean>(false);
  const { sendEvent } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('favorites');
  const showToastMessage = useContextSelector(ToastMessageContext, (v) => v.showToastMessage);
  const toastMessageId = useContextSelector(ToastMessageContext, (v) => v.toastMessageId);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const isCartPreviewOpened = useContextSelector(CartContext, (v) => v.isCartPreviewOpened);
  const setIsCartPreviewOpened = useContextSelector(CartContext, (v) => v.setIsCartPreviewOpened);
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const { favoritesUserSlug, profileSlug, options } = router?.query as {
    favoritesUserSlug: string;
    options: string; // TODO: QA testing as the API doesn't return the correct payload
    profileSlug: string;
  };
  const favoritesUserSlugId = favoritesUserSlug?.[0] ?? profileSlug?.[0] ?? '';
  const isSellerOnVacation = options?.includes('sellerOnVacation') || !!product.seller.vacation;

  const isProductInCart = (product): boolean => {
    return isProductIdInCart(productsInCart, product.id);
  };

  const isUserFavoritesUserSlugId = user?.id === favoritesUserSlugId;

  const isProductSoldOrReserved =
    isUserFavoritesUserSlugId && (product?.sold || product?.reserved || user?.id === product?.seller?.id); // only see similar - no cart button
  const alreadyInCart = isProductInCart(product); // cart button with tick icon

  const getProductPayload = (product) => ({
    product_id: product.id,
    seller_id: product?.seller?.id,
    product_universe: product?.universe?.name,
    product_category: product?.category?.name,
    product_sub_category: product?.subcategory?.name,
    product_brand: product?.brand?.name,
    product_country: product?.seller?.country,
    product_unit_price: ((product?.price?.cents ?? 0) / 100).toString(),
    product_ds_eligible: product?.dsEligible,
    product_crossborder_transaction: `${product?.dutyAndTax?.cents > 0}`,
    product_currency: product?.price?.currency,
    product_sold: product?.sold,
  });

  const handleAlreadyInCartAction = (): void => {
    setIsCartPreviewOpened(!isCartPreviewOpened);

    sendEvent({
      type: 'event',
      payload: {
        event: 'productCardAction',
        action: 'addToCart',
        product_id: product.id,
      },
    });
  };

  const addToCart = async (): Promise<void> => {
    setAtcLoading(true);
    try {
      await addToShoppingCart(product, false);
      sendAnalyticEvent('favorites_add_to_bag', {
        ...getProductPayload(product),
      });
    } catch (e) {
      logger.error('Error adding to cart - favourite product card action');
      if (showToastMessage && toastMessageId < 3) {
        showToastMessage(t('FAVORITE_PAGE.ADD_TO_CART.ERROR'), '', 3000);
      }
    } finally {
      setAtcLoading(false);
    }
  };

  const handleCartButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // Prevent default button behavior and stop event bubbling to avoid
    // triggering parent element click handlers (e.g., product card navigation)
    event.preventDefault();
    event.stopPropagation();
    if (isProductInCart(product)) {
      if (isCartPreviewOpened) {
        setIsCartPreviewOpened(!isCartPreviewOpened);
      } else {
        handleAlreadyInCartAction();
      }

      sendAnalyticEvent('favorites_add_to_bag_cta', {
        label: 'view_bag',
        ...getProductPayload(product),
      });
    } else if (!atcLoading) {
      sendAnalyticEvent('favorites_add_to_bag_cta', {
        label: 'add_to_bag_cta',
        ...getProductPayload(product),
      });
      addToCart();
    }
  };

  /**
   * Handle see similar click
   * Tracking needs to know if the user is coming from the favorites page to see similar products
   */
  const handleSeeSimilarClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    // Stop event bubbling to prevent parent element click handlers
    // from interfering with the "see similar" navigation
    event.stopPropagation();
    sendAnalyticEvent('favorites_click_cta', {
      action: 'see_similar',
      ...getProductPayload(product),
    });
  };

  /**
   * Cart button disabled / enabled based on the seller vacation status
   * Add to bag or tick icon based on the product already in cart or not
   * Show atcLoading spinner when adding to cart
   * */
  const renderAddToCartButton = () => (
    <Button
      aria-haspopup="true"
      aria-expanded={isCartPreviewOpened}
      onClick={handleCartButtonClick}
      data-cy={
        atcLoading
          ? 'favorite_product_tile_atc_loading'
          : atcLoading || (!alreadyInCart && isSellerOnVacation)
            ? 'favorite_product_tile_bag_disabled'
            : alreadyInCart
              ? 'favorite_product_tile_tick'
              : 'favorite_product_tile_bag'
      }
      variant={alreadyInCart && !atcLoading ? 'primary' : 'secondary'}
      size="small"
      className={clsx(
        atcLoading
          ? styles.productCardFavActions__cart__atcLoading
          : alreadyInCart && !atcLoading
            ? styles.productCardFavActions__cart__filled
            : styles.productCardFavActions__cart,
      )}
      disabled={atcLoading || (!alreadyInCart && isSellerOnVacation)}
    >
      {atcLoading ? (
        <div className={styles.productCardFavActions__cart__loadingContainer}>
          <Spinner className={styles.productCardFavActions__cart__loadingContainer__icon} />
        </div>
      ) : (
        <Icon
          className={clsx(styles.productCardFavActions__cart__icon)}
          name={alreadyInCart ? 'tick-small-default' : 'cart-default'}
          width={24}
          height={24}
          ariaLabel={alreadyInCart ? tA11y('A11Y.FAVORITES.VIEW_BAG') : tA11y('A11Y.FAVORITES.ADD_BAG')}
        />
      )}
    </Button>
  );

  /**
   * Render only See similar button when the product is sold or reserved
   * Render See similar and Add to cart button when the user is the owner of the favorites page and the product is not sold or reserved
   * Render See similar and Add to cart button disabled when the seller is on vacation
   * Seeing other user's favorite products, render only See similar
   */
  const renderSeeSimilarButton = () => (
    <Link
      href={`/similar-products/?productId=${product?.id}&isFromFavTile=true`}
      onClick={handleSeeSimilarClick}
      data-cy="favorite_product_tile_see_similar"
      className={clsx(
        styles.productCardFavActions__similar,
        'vc-btn',
        'vc-btn--compact',
        'vc-btn--rounded-secondary',
        'vc-btn--medium',
      )}
    >
      {t('FAVORITE_PAGE.PRODUCT_TILE.SEE_SIMILAR')}
    </Link>
  );

  const renderCTAButtons = () => {
    if (isProductSoldOrReserved) {
      return renderSeeSimilarButton();
    } else if (isUserFavoritesUserSlugId) {
      return (
        <>
          {renderSeeSimilarButton()}
          {renderAddToCartButton()}
        </>
      );
    } else {
      // product is not sold, not reserved
      if (isUserFavoritesUserSlugId) {
        return renderSeeSimilarButton();
      }
    }

    return null;
  };

  return (
    <div
      className={styles.productCardFavActions}
      data-cy={'favorite_product_card_actions'}
    >
      {renderCTAButtons()}
    </div>
  );
};

export default ProductCardFavActions;
